import { useState, useMemo, useEffect, Fragment } from 'react'
import {
  Box, Typography, Chip, TextField, InputAdornment, Alert, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper, IconButton, Collapse,
} from '@mui/material'
import { TrendingUp, Search, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material'
import ExportMenu from '../../components/common/ExportMenu'
import { useApi } from '../../hooks/useApi'
import { usePlantContext } from '../../hooks/usePlantContext'
import { formatNumber, formatDate } from '../../utils/format'
import { monthLabel } from '../../components/planningboard/boardRows'

// FAR-68: the Demand screen shows the plant's ACTIVE forecast -- the demand its
// plans are built from -- from GET /forecast. Read-only, and no demo data: with
// no forecast uploaded it says so.
//
// Rows are the FAR-65 shape: one per item for the plant that MAKES it, with
// demand summed across branch warehouses (matching how the plan is built).
// Each row expands to show which warehouses that demand came from.

export default function Demand() {
  const { get, loading, error } = useApi()
  const { plant } = usePlantContext()
  const plantCode = plant?.plantCode

  const [data, setData] = useState(null)
  const [warehouseNames, setWarehouseNames] = useState({})
  const [search, setSearch] = useState('')
  const [orderBy, setOrderBy] = useState('materialNumber')
  const [order, setOrder] = useState('asc')
  const [expanded, setExpanded] = useState({})

  useEffect(() => {
    if (!plantCode) return undefined
    let cancelled = false
    get(`/forecast?plant=${encodeURIComponent(plantCode)}`)
      .then((d) => { if (!cancelled) setData(d || null) })
      .catch(() => { if (!cancelled) setData(null) })
    return () => { cancelled = true }
  }, [plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Warehouse codes come back as plant codes (1002, 1106); name them.
  useEffect(() => {
    let cancelled = false
    get('/plants/reference')
      .then((refs) => {
        if (cancelled) return
        const names = {}
        // GET /plants/reference returns { locations: [{ code, name }], ... }.
        for (const r of refs?.locations || []) names[r.code] = r.name
        setWarehouseNames(names)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const months = data?.months || []
  const records = data?.records || []

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(col)
  }

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const filtered = records.filter((r) =>
      !q || `${r.materialNumber} ${r.description || ''}`.toLowerCase().includes(q),
    )
    const value = (r) => {
      if (orderBy === 'materialNumber') return r.materialNumber
      if (orderBy === 'description') return r.description || ''
      if (orderBy === 'annualTotal') return r.annualTotal || 0
      return r.monthlyDemand?.[orderBy] || 0 // a month column
    }
    return [...filtered].sort((a, b) => {
      const av = value(a), bv = value(b)
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return order === 'asc' ? cmp : -cmp
    })
  }, [records, search, orderBy, order])

  const shownTotal = useMemo(
    () => rows.reduce((s, r) => s + (r.annualTotal || 0), 0),
    [rows],
  )

  const warehouseLabel = (code) => (warehouseNames[code] ? `${warehouseNames[code]} (${code})` : code)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <TrendingUp color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Demand</Typography>
        <ExportMenu />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={plant?.name || 'Plant'} size="small" variant="outlined" />
        {data?.uploadedAt && (
          <Typography variant="overline" color="text.secondary">
            Uploaded {formatDate(data.uploadedAt)}
            {data.fileName ? ` · ${data.fileName}` : ''}
            {' · '}{formatNumber(data.itemCount)} items · {formatNumber(data.totalCases, 2)} cases
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
          Couldn't load the forecast: {error}
        </Alert>
      )}

      {/* A real empty state, not demo data (FAR-68). */}
      {!loading && !error && data && records.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No forecast has been uploaded for {plant?.name || 'this plant'} yet. Upload one in the
          Data Hub; plans are built from the active forecast.
        </Alert>
      )}

      {loading && !data && <Skeleton variant="rounded" height={320} />}

      {records.length > 0 && (
        <>
          <TextField
            size="small"
            placeholder="Search items…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2, width: 280 }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
          />

          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 36 }} />
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel active={orderBy === 'materialNumber'} direction={orderBy === 'materialNumber' ? order : 'asc'} onClick={() => handleSort('materialNumber')}>Item #</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel active={orderBy === 'description'} direction={orderBy === 'description' ? order : 'asc'} onClick={() => handleSort('description')}>Description</TableSortLabel>
                  </TableCell>
                  {months.map((m) => (
                    <TableCell key={m} align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      <TableSortLabel active={orderBy === m} direction={orderBy === m ? order : 'asc'} onClick={() => handleSort(m)}>
                        {monthLabel(m)}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    <TableSortLabel active={orderBy === 'annualTotal'} direction={orderBy === 'annualTotal' ? order : 'asc'} onClick={() => handleSort('annualTotal')}>Total</TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const warehouses = Object.keys(row.warehouseDemand || {}).sort()
                  const open = !!expanded[row.materialNumber]
                  return (
                    <Fragment key={row.materialNumber}>
                      <TableRow hover>
                        <TableCell>
                          {warehouses.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => setExpanded((p) => ({ ...p, [row.materialNumber]: !open }))}
                              aria-label={open ? 'Hide warehouses' : 'Show warehouses'}
                            >
                              {open ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.materialNumber}</TableCell>
                        <TableCell>{row.description || '—'}</TableCell>
                        {months.map((m) => (
                          <TableCell key={m} align="right">{formatNumber(row.monthlyDemand?.[m] || 0)}</TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(row.annualTotal || 0)}</TableCell>
                      </TableRow>

                      {/* FAR-65/68: which branch warehouses this demand came from. */}
                      {warehouses.length > 0 && (
                        <TableRow>
                          <TableCell sx={{ py: 0, border: 0 }} colSpan={months.length + 4}>
                            <Collapse in={open} unmountOnExit>
                              <Box sx={{ py: 1, pl: 4 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Demand by branch warehouse
                                </Typography>
                                <Table size="small">
                                  <TableBody>
                                    {warehouses.map((wh) => (
                                      <TableRow key={wh}>
                                        <TableCell sx={{ width: 280, color: 'text.secondary' }}>{warehouseLabel(wh)}</TableCell>
                                        {months.map((m) => (
                                          <TableCell key={m} align="right" sx={{ color: 'text.secondary' }}>
                                            {formatNumber(row.warehouseDemand[wh]?.[m] || 0)}
                                          </TableCell>
                                        ))}
                                        <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                          {formatNumber(months.reduce((s, m) => s + (row.warehouseDemand[wh]?.[m] || 0), 0))}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {formatNumber(rows.length)} of {formatNumber(records.length)} items ·
            {' '}{formatNumber(shownTotal, 2)} cases shown
          </Typography>
        </>
      )}
    </Box>
  )
}
